import { Button, IconCheck, Link, Text } from '@bbl-digital/snorre'
import { useTheme } from '@emotion/react'
import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  IMessageReciept,
  IMessageRecieptRecipient,
} from 'shared/models/Message'
import {
  SEND_EMAIL_CREATE_CONTACT_REQUESTED,
  SEND_EMAIL_SET_CREATE_CONTACT,
} from 'shared/store/sendMessage/email/actionTypes'
import {
  SEND_SMS_CREATE_CONTACT_REQUESTED,
  SEND_SMS_SET_CREATE_CONTACT,
} from 'shared/store/sendMessage/sms/actionTypes'
import {
  CloseButtonWrapper,
  Column,
  Ellipsis,
  Row,
  SuggestedContactRow,
} from '../styles'
import ContactForm from './ContactForm'

interface IProps {
  reciept: IMessageReciept
  isSms: boolean | undefined
  createContact: IMessageRecieptRecipient | null
  processing: boolean
  isSending: boolean
  error: string
  onClose: () => void
}

const RecieptInformation: FC<IProps> = ({
  reciept,
  isSms,
  createContact,
  processing,
  isSending,
  onClose,
}) => {
  const dispatch = useDispatch()
  const { push } = useHistory()
  const [showAll, setShowAll] = useState(false)
  const { success } = useTheme()

  const previewUrl = `/kommunikasjon/meldingsdetaljer/${
    isSms ? 'sms' : 'epost'
  }/${reciept?.recipients[0]?.messageId}`

  const validRecipients = reciept?.recipients?.filter((x) =>
    isSms ? x.validPhonenumber : x.validEmail
  )
  const invalidRecipients = reciept?.recipients?.filter((x) =>
    isSms ? !x.validPhonenumber : !x.validEmail
  )

  const contactSuggestions = validRecipients?.filter(
    (x) => !x.isExistingContact
  )

  const allRecipients = validRecipients
    ? showAll || validRecipients.length <= 3
      ? validRecipients
      : validRecipients.slice(0, 3)
    : []

  const setCreateContact = (contact: IMessageRecieptRecipient | null) => {
    dispatch({
      type: isSms ? SEND_SMS_SET_CREATE_CONTACT : SEND_EMAIL_SET_CREATE_CONTACT,
      payload: contact,
    })
  }

  return (
    <>
      {createContact ? (
        <ContactForm
          contact={createContact}
          processing={processing}
          onSubmit={(values) =>
            dispatch({
              type: isSms
                ? SEND_SMS_CREATE_CONTACT_REQUESTED
                : SEND_EMAIL_CREATE_CONTACT_REQUESTED,
              payload: values,
            })
          }
          onCancel={() => setCreateContact(null)}
          isSms={isSms}
        />
      ) : (
        <>
          <Text semibold>
            {isSending
              ? `Sender ${isSms ? 'SMS-en' : 'e-posten'} `
              : `Denne ${isSms ? 'SMS-en' : 'e-posten'}  ble sendt `}{' '}
            til {validRecipients?.length || 0} person
            {(!validRecipients || validRecipients.length > 1) && 'er'}
          </Text>

          <Column gap="0.25em">
            {allRecipients.map((recipient) => (
              <Ellipsis key={recipient.id}>
                <Text data-testid="recipients">
                  {recipient.name
                    ? `${recipient.name} (${
                        isSms ? recipient.phonenumber : recipient.email
                      })`
                    : isSms
                    ? recipient.phonenumber
                    : recipient.email}
                </Text>
              </Ellipsis>
            ))}

            {validRecipients && validRecipients.length > 3 && (
              <Link
                nopadding
                textAlign="left"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? 'Vis færre' : 'Vis alle'}
              </Link>
            )}
          </Column>

          <Link
            nopadding
            textAlign="left"
            href={
              window.ReactNativeWebView
                ? undefined
                : `${window.config.baseUrl}${previewUrl}`
            }
            onClick={
              window.ReactNativeWebView ? () => push(previewUrl) : undefined
            }
            target={window.ReactNativeWebView ? undefined : '_blank'}
          >
            Åpne sendt {isSms ? 'SMS' : 'e-post'}
          </Link>

          {contactSuggestions && contactSuggestions.length > 0 && (
            <>
              <Text semibold>Forslag til nye kontakter</Text>

              {contactSuggestions.map((contact, i) => (
                <SuggestedContactRow key={i}>
                  <Text
                    data-testid="contacts"
                    title={isSms ? contact.phonenumber : contact.email}
                  >
                    {isSms ? contact.phonenumber : contact.email}
                  </Text>
                  {contact.isAdded ? (
                    <Text>
                      <Row gap="0.25em">
                        <IconCheck color={success} size="20px" /> Lagt til
                      </Row>
                    </Text>
                  ) : (
                    <>
                      <Link
                        nopadding
                        textAlign="left"
                        onClick={() => setCreateContact(contact)}
                      >
                        Legg til
                      </Link>
                    </>
                  )}
                </SuggestedContactRow>
              ))}
            </>
          )}

          {invalidRecipients && invalidRecipients.length > 0 && (
            <>
              <Text semibold>
                Det er {invalidRecipients.length} person
                {invalidRecipients.length > 1 && 'er'} som ikke har mottatt
                {isSms ? ' meldingen' : ' mailen'}, da de ikke har registrert{' '}
                {isSms ? 'et gyldig norsk mobilnummer' : 'en e-postadresse'}.
              </Text>

              <Column gap="0.25em">
                {invalidRecipients.map((person) => (
                  <Text key={person.id} data-testid="not-sent">
                    {person.name || (isSms ? person.phonenumber : person.email)}
                  </Text>
                ))}
              </Column>
            </>
          )}

          <CloseButtonWrapper>
            <Button onClick={onClose}>Lukk</Button>
          </CloseButtonWrapper>
        </>
      )}
    </>
  )
}

export default RecieptInformation
